import { useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GrupoEquivalenciaDetalle } from "@/components/GrupoEquivalenciaDetalle";
import { Pencil, Trash2, Check, X, ListTree } from "lucide-react";

export const GruposEquivalenciaDialog = ({ open, onClose, grupos, onChanged }) => {
  const [nuevo, setNuevo] = useState("");
  const [editando, setEditando] = useState(null); // {id, nombre}
  const [detalle, setDetalle] = useState(null); // grupo abierto

  const cerrar = (v) => { if (!v) setDetalle(null); onClose(v); };

  const crear = async () => {
    try { await api.post("/grupos-equivalencia", { nombre: nuevo }); setNuevo(""); onChanged(); toast.success("Grupo creado"); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const guardarEdicion = async () => {
    try { await api.patch(`/grupos-equivalencia/${editando.id}`, { nombre: editando.nombre }); setEditando(null); onChanged(); toast.success("Grupo actualizado"); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este grupo? Los repuestos que lo usan quedarán sin grupo.")) return;
    try { await api.delete(`/grupos-equivalencia/${id}`); onChanged(); toast.success("Grupo eliminado"); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle>{detalle ? `Grupo: ${detalle.nombre}` : "Grupos de equivalencia"}</DialogTitle>
          <DialogDescription className="text-xs">Los repuestos que compartan el mismo grupo son intercambiables entre sí (ej.: "Kit de cilindro 150cc").</DialogDescription>
        </DialogHeader>
        {detalle ? (
          <GrupoEquivalenciaDetalle grupo={detalle} onBack={() => setDetalle(null)} onChanged={onChanged} />
        ) : (
        <>
        <div className="flex gap-2">
          <Input data-testid="nuevo-grupo-eq-input" placeholder="Nombre del grupo" value={nuevo} onChange={(e) => setNuevo(e.target.value)} />
          <Button data-testid="crear-grupo-eq-btn" onClick={crear} disabled={!nuevo.trim()}>Agregar</Button>
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {grupos.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay grupos.</p>}
          {grupos.map((g) => (
            <div key={g.id} className="flex items-center gap-2 border border-border rounded-sm px-3 py-1.5 text-sm">
              {editando?.id === g.id ? (
                <>
                  <Input data-testid={`editar-grupo-eq-input-${g.id}`} className="h-7" value={editando.nombre}
                    onChange={(e) => setEditando({ ...editando, nombre: e.target.value })} autoFocus />
                  <Button data-testid={`guardar-grupo-eq-${g.id}`} variant="ghost" size="icon" className="h-7 w-7 text-green-700" onClick={guardarEdicion}><Check size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditando(null)}><X size={14} /></Button>
                </>
              ) : (
                <>
                  <button data-testid={`abrir-grupo-eq-${g.id}`} className="flex-1 text-left hover:underline cursor-pointer"
                    onClick={() => setDetalle(g)} title="Ver repuestos del grupo">{g.nombre}</button>
                  <Button data-testid={`ver-repuestos-grupo-${g.id}`} variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetalle(g)} title="Ver repuestos"><ListTree size={13} /></Button>
                  <Button data-testid={`editar-grupo-eq-${g.id}`} variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditando({ id: g.id, nombre: g.nombre })}><Pencil size={13} /></Button>
                  <Button data-testid={`eliminar-grupo-eq-${g.id}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => eliminar(g.id)}><Trash2 size={13} /></Button>
                </>
              )}
            </div>
          ))}
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
